// docsContent.tsx - every page of the in-app product documentation.
//
// Written for creators and players, not engineers: what each part of Studio
// does, how to use it, and what its limits are. Facts here describe the
// product as it ships today; anything not yet live is said to be not yet
// live rather than left out or promised. The single source of truth for
// which authored gameplay systems run in published games is
// config/gameplayReadiness.ts, quoted where relevant so the two never drift.
import { Link } from '../design/router';
import { GAMEPLAY_READINESS } from '../config/gameplayReadiness';
import type { DocPage } from './docsModel';

const A = ({ to, children }: { to: string; children: string }) => <Link to={to} style={{ color: 'var(--ss-accent)' }}>{children}</Link>;

const readiness = (id: string) => GAMEPLAY_READINESS.find((e) => e.id === id);
const questsLive = readiness('quests')?.status === 'live';

/* ── Getting started ──────────────────────────────────────────────────── */
const overview: DocPage = {
  path: '/docs', group: 'Getting started', label: 'Overview',
  title: 'Soulstack Studio documentation',
  summary: 'What Studio is, what you need, and where to start.',
  keywords: ['introduction', 'start', 'requirements', 'browser'],
  blocks: [
    { kind: 'p', text: 'Soulstack Studio is a game studio that runs in your browser. You build 2D or 3D games in a visual editor, playtest them in the same tab, and publish them to a hosted address that players can open in a browser or install as an app.' },
    { kind: 'h2', text: 'What you need' },
    { kind: 'ul', items: [
      'A current desktop browser. Chrome, Edge, Firefox and Safari all work.',
      'A screen at least 860 pixels wide for the editor. Narrower screens can still playtest and manage projects.',
      'An account, created with Google or an email address.',
    ]},
    { kind: 'p', text: 'Players need only a browser. They do not need an account to play a published game.' },
    { kind: 'h2', text: 'How the pieces fit' },
    { kind: 'table', head: ['Part', 'What it is for'], rows: [
      ['Dashboard', 'Your projects. Create, open, duplicate or import them.'],
      ['Project workspace', 'Everything around a project: overview, assets, publishing, versions, wiki, players and settings.'],
      ['Editor', 'Where the game is made. Five workspaces: Scene, Art & Assets, Systems, Setup and Code.'],
      ['Preview', 'Playtest the project in the tab, with the same runtime players get.'],
      ['Play page', 'The public page every published game gets, with a Play button, sharing and an optional wiki.'],
      ['Showcase', 'Public games, developer profiles and wikis.'],
    ]},
    { kind: 'h2', text: 'Where to go next' },
    { kind: 'ul', items: [
      <><A to="/docs/quickstart">Quickstart</A>: from a new account to a published game.</>,
      <><A to="/docs/editor">The editor at a glance</A>: the shell, the workspaces and the top bar.</>,
      <><A to="/docs/publishing">Publishing</A>: addresses, visibility, versions and installing.</>,
      <><A to="/docs/reference/shortcuts">Keyboard shortcuts</A>: every binding in one place.</>,
    ]},
  ],
};

const quickstart: DocPage = {
  path: '/docs/quickstart', group: 'Getting started', label: 'Quickstart',
  title: 'Quickstart',
  summary: 'From a new account to a published, installable game.',
  keywords: ['tutorial', 'first game', 'begin'],
  blocks: [
    { kind: 'steps', items: [
      { title: 'Create an account', body: <p><A to="/signup">Sign up</A> with Google or an email address and a password. Email sign-ups confirm the address with a code. You land on the dashboard.</p> },
      { title: 'Create a project', body: <p>Press New project. Pick a starting point (Blank, Gallery, Village or Platformer), choose 2D or 3D, and name it. Village and Platformer are 2D starters. See <A to="/docs/projects">Projects and templates</A>.</p> },
      { title: 'Open the editor', body: <p>The project opens in the Scene workspace. The viewport is in the middle with its tools docked above it, the scene structure on the left and the properties of whatever you select on the right.</p> },
      { title: 'Place something', body: <p>Open the Assets tab in the bottom dock, pick a model and click in the viewport to place it. Press <code>G</code> to move it, <code>R</code> to rotate, <code>S</code> to scale. In a 2D project, switch to Pixel Studio in Art & Assets and paint a tile or a sprite instead.</p> },
      { title: 'Playtest', body: <p>Press Preview in the top bar. You are in the game, with the same runtime players will get. Press <code>Esc</code> to come back with nothing lost.</p> },
      { title: 'Publish', body: <p>Press Publish, choose Public, Unlisted or Off, and press Publish v1. The game is live at its own address, and the address is yours to share.</p> },
    ]},
    { kind: 'code', text: 'https://sstk.studio/play/your-game', caption: 'Every published game gets a permanent address like this.' },
    { kind: 'callout', text: <>Every publish is a snapshot you can restore later. Change the address, the visibility and the version history from the project's <A to="/docs/publishing">Publish page</A>.</> },
    { kind: 'h2', text: 'Next' },
    { kind: 'ul', items: [
      <>Add gameplay with <A to="/docs/editor/logic">Logic rules</A>, no code needed.</>,
      <>Turn on <A to="/docs/multiplayer">multiplayer</A> by setting the game type to Co-op or Versus.</>,
      <>Tell players how to <A to="/docs/publishing/install">install the game</A> on their phone or desktop.</>,
    ]},
  ],
};

const projects: DocPage = {
  path: '/docs/projects', group: 'Getting started', label: 'Projects and templates',
  title: 'Projects and templates',
  summary: 'Starting points, 2D versus 3D, and the project workspace.',
  keywords: ['blank', 'gallery', 'village', 'platformer', 'import', 'duplicate', 'export', 'sstk', 'storage'],
  blocks: [
    { kind: 'h2', text: 'Starting points' },
    { kind: 'table', head: ['Template', 'What you get', 'Projection'], rows: [
      ['Blank', 'An empty world with the core asset pack and the neutral biped character.', '2D or 3D'],
      ['Gallery', 'A furnished 3D room with shelving, a table and chairs, and columns to rearrange.', '3D'],
      ['Village', 'A top-down street of houses, a pond and trees.', '2D'],
      ['Platformer', 'A side-view level with a hazard, a checkpoint, bonus platforms and a goal.', '2D'],
      ['Import project', 'Restore a project from an exported .sstk file.', 'As exported'],
    ]},
    { kind: 'h2', text: '2D or 3D' },
    { kind: 'p', text: 'A project is either 2D or 3D. The choice decides which tools appear: Model Studio, Characters, Animation Studio, Gunsmith Kit, FX and Cutscenes are 3D only; Pixel Studio is 2D only. Everything else is shared.' },
    { kind: 'p', text: 'You can switch later from Setup, under Camera. Nothing is deleted when you switch; the tools for the other projection simply become unreachable until you switch back.' },
    { kind: 'h2', text: 'The project workspace' },
    { kind: 'p', text: 'Every project has a workspace with seven pages, reached from the dashboard or from the project name in the editor.' },
    { kind: 'table', head: ['Page', 'What it is for'], rows: [
      ['Overview', 'Live status, cover image, play address, storage and quick links back into the editor.'],
      ['Assets', 'Uploaded models, images and audio. Rename, place or delete them.'],
      ['Publish', 'Publish a version, set visibility, run the pre-flight audit, manage the player pool.'],
      ['Versions', 'Every published version, plus automatic scene snapshots you can compare and restore.'],
      ['Wiki', 'A hosted multi-page wiki for the game.'],
      ['Players', 'Player counts, match history and analytics for multiplayer projects.'],
      ['Settings', 'Name, play address slug, export targets, project export and deletion.'],
    ]},
    { kind: 'h2', text: 'Cover image' },
    { kind: 'p', text: 'Upload a PNG, JPEG or WebP up to 2 MB on the Overview page. It becomes the key art on the play page and the showcase card. Without one, a generated cover is used.' },
    { kind: 'h2', text: 'Duplicate, export and import' },
    { kind: 'ul', items: [
      'Duplicate from the dashboard copies the whole project, including its map, documents, characters and asset references.',
      'Export project (.sstk) on the Settings page downloads the map, every editor document and the settings. Uploaded files are not included.',
      'Import project on the New project dialog restores an exported file as a new project.',
    ]},
    { kind: 'h2', text: 'Storage' },
    { kind: 'p', text: 'Each project has 10 GB of storage for uploads. The meter in the workspace sidebar shows how much is used.' },
  ],
};

/* ── The editor ───────────────────────────────────────────────────────── */
const editor: DocPage = {
  path: '/docs/editor', group: 'The editor', label: 'At a glance',
  title: 'The editor at a glance',
  summary: 'The shell, the five workspaces, the top bar and the dock.',
  keywords: ['workspace', 'panel', 'dock', 'top bar', 'save', 'autosave', 'distraction free', 'layout'],
  blocks: [
    { kind: 'p', text: 'The editor is one window with five named workspaces along the top. Each workspace is a set of dockable panels. Drag a panel to move it, and the layout is remembered per project.' },
    { kind: 'image', placeholder: 'Editor overview with the top bar, workspace tabs, viewport and panels labelled', height: 360 },
    { kind: 'h2', text: 'Workspaces' },
    { kind: 'table', head: ['Workspace', 'Panels', 'Guide'], rows: [
      ['Scene', 'Edit, Map Builder, Environment', <A to="/docs/editor/scene">Scene</A>],
      ['Art & Assets', 'Model Studio, Characters, Animation Studio, Gunsmith Kit (3D); Pixel Studio (2D)', <A to="/docs/editor/art">Art & Assets</A>],
      ['Systems', 'Logic, HUD, Audio, FX (3D), Game Data, Cutscenes (3D), Intro Sequences', <A to="/docs/editor/systems">Systems</A>],
      ['Setup', 'Rules, Camera, World, Meta', <A to="/docs/editor/setup">Setup</A>],
      ['Code', 'Scripts, Editor, Run Output', <A to="/docs/editor/code">Code</A>],
    ]},
    { kind: 'h2', text: 'The top bar' },
    { kind: 'ul', items: [
      'The Soulstack mark returns to the Scene workspace. The project name opens the project workspace.',
      'A chip shows the current mode and whether the project is 2D or 3D.',
      'Save writes the scene now. Changes also save automatically a moment after you stop editing, and the status chip reads Saved, Saving, Backed up or Save failed.',
      'Distraction-free mode hides the workspace tabs, the dock and the status bar. Toggle it with Ctrl or Cmd + Shift + F.',
      <>Preview opens the playtest. See <A to="/docs/editor/preview">Playtesting</A>.</>,
      <>Publish opens the publish panel. See <A to="/docs/publishing">Publishing</A>.</>,
    ]},
    { kind: 'h2', text: 'The bottom dock' },
    { kind: 'table', head: ['Tab', 'What it holds'], rows: [
      ['Assets', 'The shared library and your uploads in 3D projects; the core pack, uploads and sprites in 2D projects. Click an asset, then click in the viewport to place it.'],
      ['Prefabs', 'Ready-made groups (Tactical, Cover, Lighting) and any prefabs you have saved from the scene.'],
      ['Console', 'A script console for the live Preview session. Open it with the backtick key.'],
      ['Output', 'A summary of the scene and the last save.'],
      ['Profiler', 'Not yet available. Frame, draw-call and memory budgets will appear here.'],
    ]},
    { kind: 'p', text: 'Drag the dock edge to resize it, or collapse it to a strip. The status bar underneath shows the engine version, grid and snap state, and the live frame rate.' },
    { kind: 'h2', text: 'Screen size' },
    { kind: 'p', text: 'The editor needs a window at least 860 pixels wide. On a narrower screen it offers to open Preview instead, so you can still playtest from a phone or tablet.' },
    { kind: 'callout', text: <>Press <code>?</code> anywhere in the editor for a searchable list of shortcuts, or see <A to="/docs/reference/shortcuts">Keyboard shortcuts</A>.</> },
  ],
};

const scene: DocPage = {
  path: '/docs/editor/scene', group: 'The editor', label: 'Scene',
  title: 'Scene: Edit, Map Builder and Environment',
  summary: 'Place and light objects, draw structure, and set the sky.',
  keywords: ['light', 'lighting', 'trigger', 'marker', 'spawn', 'floor', 'wall', 'stairs', 'room', 'gizmo', 'snap', 'grid', 'fly', 'decal', 'inspector', 'environment', 'preset', 'sun', 'fog'],
  blocks: [
    { kind: 'h2', text: 'Edit' },
    { kind: 'p', text: 'Edit is the main viewport. The scene tree on the left lists objects, lighting, gameplay markers, decals and structure. Select anything there or in the viewport, and its properties appear in the inspector on the right.' },
    { kind: 'h3', text: 'Viewport tools' },
    { kind: 'table', head: ['Tool', 'Key', 'What it does'], rows: [
      ['Select', 'V', 'Pick objects.'],
      ['Move', 'G', 'Drag the selection. Arrow keys nudge by one grid step; Shift nudges ten.'],
      ['Rotate', 'R', 'Turn the selection. Q and E rotate by 45 degrees.'],
      ['Scale', 'S', 'Resize the selection.'],
      ['Snap', '', 'Snap movement to the grid.'],
      ['Grid', '', 'Show or hide the ground grid (3D only).'],
      ['Fly', '', 'Free camera: WASD and the mouse move the view, click to exit (3D only).'],
      ['Floor', '', 'Step between floors on a multi-floor map.'],
      ['Vis', '', 'Hide or show categories of object while you work.'],
    ]},
    { kind: 'h3', text: 'Placing' },
    { kind: 'p', text: 'Open the Assets dock, click a model and click the floor. Escape cancels a pending placement. Press F to focus the camera on the selection, Ctrl or Cmd + D to duplicate it, and Delete to remove it. Deleting more than three objects at once asks first.' },
    { kind: 'h3', text: 'Lights' },
    { kind: 'p', text: 'Add point lights for lamps and fires, spot lights for doorways and reveals, and an ambient override where a room needs its own base level. Each has colour, intensity and range; spots also have an angle and a shadow toggle. The sun and sky come from Environment.' },
    { kind: 'h3', text: 'Markers and triggers' },
    { kind: 'ul', items: [
      'Markers: Spawn, Checkpoint, Event and Custom. A spawn marker is where players start.',
      'Triggers: Teleport, Event, Damage and Custom zones. Teleport moves the player to a target floor and position. Every kind also reaches your Logic rules as a player-enters or player-leaves event.',
      'Give each trigger a zone id. That id is what a Logic rule matches on.',
    ]},
    { kind: 'h3', text: 'The inspector' },
    { kind: 'p', text: 'Objects have a name, position, rotation and scale, a material preset, emissive and roughness. A collidable flag shows whether the asset blocks movement. In a 2D project, the fields that have no effect on a flat scene are dimmed and say why.' },
    { kind: 'h2', text: 'Map Builder' },
    { kind: 'p', text: 'Map Builder is where structure is drawn. Floors are listed on the left; the viewport shows the active floor from above.' },
    { kind: 'ul', items: [
      'Draw wall: click and drag a wall segment.',
      'Place panel: a flat floor or ceiling panel.',
      'Draw room: click and drag a rectangle. Walls, floor and openings are derived for you.',
      'Place stairs: drag a footprint to make a flight between floors. Set a tread count for steps, or leave it at zero for a ramp.',
      'Erase: remove structure.',
    ]},
    { kind: 'p', text: 'Each floor has its own name, wall height, grid snap and wall thickness. Map Builder keeps its own undo history (Ctrl or Cmd + Z), separate from object edits. Deleting a floor also removes the objects, lights and triggers on it, and asks first.' },
    { kind: 'h2', text: 'Environment' },
    { kind: 'p', text: 'Environment sets the sky, sun, time of day and fog for the whole scene. Start from a preset and adjust the sliders; everything renders live in Edit and Preview.' },
    { kind: 'table', head: ['Preset', 'Feel'], rows: [
      ['Midday clear', 'Bright, neutral, long visibility.'],
      ['Golden hour', 'Warm low sun, long shadows.'],
      ['Overcast', 'Soft light, no hard shadows.'],
      ['Night ops', 'Dark with cool ambient light.'],
      ['Dusk storm', 'Low light with heavy fog.'],
    ]},
    { kind: 'callout', text: 'Name your lights and triggers. lamp_bay_a is findable in the scene tree and in a Logic rule; PointLight_17 is not.' },
  ],
};

const art: DocPage = {
  path: '/docs/editor/art', group: 'The editor', label: 'Art & Assets',
  title: 'Art & Assets',
  summary: 'Model Studio, Pixel Studio, uploads and the shared library.',
  keywords: ['model', 'primitive', 'boolean', 'csg', 'sprite', 'tilemap', 'tile', 'pixel', 'palette', 'spritesheet', 'upload', 'glb', 'library', 'material', 'texture', 'gunsmith', 'weapon'],
  blocks: [
    { kind: 'h2', text: 'Model Studio (3D)' },
    { kind: 'p', text: 'Build props from primitives: box, sphere, cylinder, cone, wedge and extrude. Each element has a position, size, rotation and colour, and a boolean mode of Add, Subtract or Intersect, so you can cut windows and hollow shapes. Colours come from the same palettes as Pixel Studio.' },
    { kind: 'ul', items: [
      'Save to assets exports the model as a GLB into the project, ready to place.',
      'Import .glb brings an existing model straight into the project assets.',
      'One hundred model units make one metre in the scene.',
    ]},
    { kind: 'h2', text: 'Pixel Studio (2D)' },
    { kind: 'p', text: 'Pixel Studio has three views: Sprite, Tilemap and Place.' },
    { kind: 'h3', text: 'Sprite' },
    { kind: 'ul', items: [
      'Canvas sizes of 16, 24, 32, 48 or 64 pixels, or a custom size. Shrinking crops every frame and asks first.',
      'Tools: pencil (B), eraser (E), fill (G) and pick (I), with undo and redo.',
      'Palettes: Midnight, Ember and Verdant are built in. Make your own and add or remove colours.',
      'Clips: named animations with frames, a loop toggle, speed and onion skinning.',
      'Import art: bring in a PNG or a spritesheet. A sheet is sliced into cells of the size you give, from 4 to 128 pixels, up to 512 cells, and named for you.',
    ]},
    { kind: 'h3', text: 'Tilemap' },
    { kind: 'p', text: 'Set the map size and paint on three layers: Background, Foreground and Main. Only Main has collision; the other two are visual. Choose Top-down or Platformer, which also sets the camera in Setup.' },
    { kind: 'table', head: ['Collision kind', 'Effect', 'Modes'], rows: [
      ['None', 'Walkable.', 'Both'],
      ['Solid', 'Blocks the player.', 'Both'],
      ['One-way platform', 'Passable from below, stood on from above.', 'Platformer'],
      ['Hazard', 'Damages on contact.', 'Platformer'],
      ['Checkpoint', 'Sets the respawn point.', 'Platformer'],
      ['Crumble', 'Breaks after being stood on.', 'Platformer'],
      ['Phase barrier', 'Blocks until phased through.', 'Platformer'],
      ['Conveyor left / right', 'Carries the player sideways.', 'Platformer'],
    ]},
    { kind: 'p', text: 'Six tiles are built in: grass, water, stone, sand, lava and void. Add your own with their own pixel art and collision kind.' },
    { kind: 'h3', text: 'Place' },
    { kind: 'p', text: 'Place sprites as decoration, NPCs, enemies or items, set the player spawn, and drop triggers with an event name. Click to place, click again to remove, drag to cover many cells. An NPC or enemy can have a patrol of waypoints with a dwell time at each.' },
    { kind: 'h2', text: 'Uploads' },
    { kind: 'table', head: ['Type', 'Formats', 'Limit'], rows: [
      ['Models', 'GLB, a single scene with triangulated meshes', '10 MB each'],
      ['Images and textures', 'PNG, JPEG, WebP', '8 MB each'],
      ['Audio', 'MP3, OGG, WAV', '5 MB each'],
    ]},
    { kind: 'p', text: 'Models upload from the project Assets page or Model Studio. Images and audio are added from the panel that uses them. Every upload is checked on the way in and is available to place immediately.' },
    { kind: 'h2', text: 'The shared library' },
    { kind: 'p', text: 'The Assets dock in a 3D project includes a shared library of thousands of ready models. Search it, filter by category, author or bundle, hover to preview, and place a model like any other. Animated library models play their own clips only; they do not take clips from the shared character rig.' },
    { kind: 'h2', text: 'Materials and textures' },
    { kind: 'p', text: 'FX in the Systems workspace holds material presets such as worn metal, raw concrete and tinted glass, each with albedo, normal, roughness and metalness maps. Presets you author there appear in the Edit inspector\'s material picker.' },
    { kind: 'h2', text: 'Gunsmith Kit (3D)' },
    { kind: 'p', text: 'Gunsmith Kit builds weapons for a 3D project. Pick a chassis, mount catalogued components on its sockets, choose a finish per part (with glow on sights, optics and energy parts) from six skin presets or your own mix, and shape the shot sound from a crack, a body and a tail. Weapons are saved as drafts and published per project, with undo, redo and revert. A skin can be exported as a share code.' },
  ],
};

const characters: DocPage = {
  path: '/docs/editor/characters', group: 'The editor', label: 'Characters',
  title: 'Characters and animation',
  summary: 'The roster, the neutral biped, custom rigs and patrols.',
  keywords: ['rig', 'biped', 'clip', 'animation', 'pose', 'bone', 'auto-rig', 'retarget', 'patrol', 'npc', 'roster', 'placement'],
  blocks: [
    { kind: 'h2', text: 'The roster' },
    { kind: 'p', text: 'Characters (3D projects) lists the project roster. Each character has a name, a team (Player, Antagonist or NPC), a body and any number of placements in the world.' },
    { kind: 'ul', items: [
      'Body: the neutral biped, the character\'s own imported rig, or the rigged body of another roster entry.',
      'Placements: floor, position and facing. Add a patrol with waypoints and a dwell time at each, and choose Loop, Ping-pong or Once.',
      'Pose in Animation Studio jumps to the turntable with that character loaded.',
    ]},
    { kind: 'h2', text: 'The neutral biped' },
    { kind: 'p', text: 'Every project ships with a fully rigged, deliberately styleless biped and 45 animation clips. Poses and clips built against it work across the whole roster.' },
    { kind: 'table', head: ['Group', 'Clips'], rows: [
      ['Locomotion', 'Idle, walk, run, crouch and turns'],
      ['Traversal', 'Jumps, vaults, climbs and landings'],
      ['Combat', 'Pistol, melee and magic sets'],
      ['Reactions', 'Hits and deaths'],
      ['Social', 'Waves, nods and gestures'],
      ['Tasks, Vehicle, Swim', 'Working, seated and swimming'],
    ]},
    { kind: 'h2', text: 'Animation Studio' },
    { kind: 'p', text: 'Pick a model on the left, pick a clip, and it plays on the turntable in the middle. Drag to orbit, scroll to zoom, adjust the playback speed on the right. What you see is what players see: the runtime plays the same clips on the same rig.' },
    { kind: 'ul', items: [
      'Attachment: attach one of your uploaded models as a right-hand prop and it rides every clip (stock rig only).',
      'Library models: the shared library\'s animated models appear here too and play their own clips.',
      'Clips from saved poses and direct bone posing are not yet available.',
    ]},
    { kind: 'h2', text: 'Your own models' },
    { kind: 'p', text: 'Upload a GLB and it appears in Animation Studio marked No rig yet. Two paths make it play the shared clips:' },
    { kind: 'ul', items: [
      'Map bones: for a model that already has a skeleton, map its bones onto the shared skeleton.',
      'Auto-rig: for a model without one, place landmark markers on the mesh and a skeleton is built for you.',
    ]},
    { kind: 'p', text: 'A rigged model becomes a custom rig on the roster and previews live on the turntable. Placed characters, their poses and their patrols all reach players in the published game.' },
  ],
};

const logic: DocPage = {
  path: '/docs/editor/logic', group: 'The editor', label: 'Logic rules',
  title: 'Logic rules',
  summary: 'Gameplay as when, if, then sentences, with no code.',
  keywords: ['rule', 'event', 'condition', 'action', 'variable', 'score', 'item', 'quest', 'win', 'lose', 'announce', 'zone', 'trigger'],
  blocks: [
    { kind: 'p', text: 'Logic lives in the Systems workspace. A rule is one sentence: when an event happens, if some conditions hold, then do some actions. Rules run wherever your game runs, in Preview and in the published game, and in multiplayer their results reach every player.' },
    { kind: 'image', placeholder: 'A rule with its When, If and Then cards', height: 300 },
    { kind: 'h2', text: 'When: events' },
    { kind: 'table', head: ['Event', 'Fires'], rows: [
      ['Round starts', 'At the start of a round or session.'],
      ['Round ends', 'When a round ends.'],
      ['Player joins', 'When a player connects.'],
      ['Player leaves', 'When a player disconnects.'],
      ['Score changes', 'Whenever a score changes.'],
      ['Player enters zone', 'When a player enters a trigger zone.'],
      ['Player leaves zone', 'When a player leaves a trigger zone.'],
      ['Every second', 'Once a second, for timers and checks.'],
    ]},
    { kind: 'h2', text: 'If: conditions' },
    { kind: 'p', text: 'A rule can have no conditions (Always) or several. Each compares a fact with a value using is, is not, is below, is at most, is above or is at least.' },
    { kind: 'table', head: ['Fact', 'What it reads'], rows: [
      ['Round number', 'The current round.'],
      ['Team scores', 'Either team\'s score in a Versus game.'],
      ['Player count', 'How many players are connected.'],
      ['Event team, Event zone, Event player', 'Details of the event that fired the rule.'],
      ['Match variable', 'A named number your rules set.'],
      ['Item count', 'How many of a named item the player holds.'],
      ['Quest status', 'Inactive, active, complete or failed.'],
    ]},
    { kind: 'h2', text: 'Then: actions' },
    { kind: 'table', head: ['Action', 'Effect'], rows: [
      ['Announce', 'Shows a message to players.'],
      ['Set variable, Add to variable', 'Writes a named match variable.'],
      ['Emit event', 'Fires a custom event other rules can listen for.'],
      ['Give item, Take item', 'Changes the player\'s inventory, using items from Game Data.'],
      ['Start, Complete, Fail quest', 'Changes a quest\'s status.'],
      ['Win game, Lose game', 'Ends the match with a result screen.'],
    ]},
    { kind: 'h2', text: 'Wiring a trigger to a rule' },
    { kind: 'steps', items: [
      { title: 'Place the zone', body: <p>In Scene, place a trigger and give it a zone id, for example <code>skiff_bay</code>. In a 2D project, place a trigger in Pixel Studio\'s Place view and give it an event name.</p> },
      { title: 'Write the rule', body: <p>In Logic, add a rule. Set When to Player enters zone. Add an If: Event zone is <code>skiff_bay</code>. Add a Then, for example Announce.</p> },
      { title: 'Test it', body: <p>Press Preview and walk into the zone. The rule fires and the announcement appears in the HUD feed.</p> },
    ]},
    { kind: 'callout', text: <>Zone ids and item names are picked from your own project, so a rule cannot point at something that does not exist. The Reference panel beside the editor lists every event, fact and action that runs today.</> },
    { kind: 'h2', text: 'What runs in a published game' },
    { kind: 'ul', items: GAMEPLAY_READINESS.map((e) => <><strong>{e.system}</strong>: {e.detail}</>) },
    ...(questsLive ? [] : [{ kind: 'p' as const, text: 'Quest status still works inside rules as a condition and an action; what is not yet live is showing quest progress to the player.' }]),
    { kind: 'p', text: <>For more than rules can express, see <A to="/docs/editor/code">Code and scripting</A>.</> },
  ],
};

const systems: DocPage = {
  path: '/docs/editor/systems', group: 'The editor', label: 'Systems',
  title: 'Systems: HUD, Audio, FX, Game Data, Cutscenes, Intro Sequences',
  summary: 'Everything around the rules: what players see and hear.',
  keywords: ['hud', 'widget', 'health', 'timer', 'minimap', 'mixer', 'music', 'zone', 'falloff', 'particles', 'material', 'items', 'dialogue', 'quest', 'cutscene', 'camera', 'intro', 'timeline'],
  blocks: [
    { kind: 'h2', text: 'HUD' },
    { kind: 'p', text: 'Lay out what players see over the game. Turn widgets on or off, then set each one\'s anchor, scale and opacity, and watch the stage update.' },
    { kind: 'table', head: ['Widget', 'Shows'], rows: [
      ['Health bar', 'The player\'s health, live.'],
      ['Resource counter', 'Ammo or another resource, live.'],
      ['Timer', 'Round or session time.'],
      ['Minimap', 'A top-down view of the map.'],
      ['Event feed', 'Announcements and rule results.'],
      ['Inventory hotbar', 'Held items.'],
      ['Dialogue box', 'The first line of authored dialogue. Triggering specific lines from gameplay is not yet wired.'],
    ]},
    { kind: 'h2', text: 'Audio' },
    { kind: 'ul', items: [
      'Library: upload MP3, OGG or WAV files up to 5 MB.',
      'Mixer: Master, Music, SFX and Ambience levels.',
      'Zones: place a sound in the world with a position, a radius and a smooth or linear falloff, so it gets louder as players approach.',
    ]},
    { kind: 'h2', text: 'FX (3D)' },
    { kind: 'p', text: 'Materials holds presets with albedo, normal, roughness and metalness maps; textures and normal maps can be generated from an albedo. Particles holds the particle presets used by effects.' },
    { kind: 'h2', text: 'Game Data' },
    { kind: 'p', text: 'Three databases: Items, Quests and Dialogue. Item names feed the Give item and Take item actions in Logic. Quests are used by quest conditions and actions. The first dialogue line shows in the Dialogue widget.' },
    { kind: 'h2', text: 'Cutscenes (3D)' },
    { kind: 'p', text: 'A cutscene is a short cinematic of up to six seconds on five tracks: camera, actors, post effects, audio and signals. The camera can pan between points or track a placed character. Post effects are bloom, chromatic aberration, noise and vignette. The project\'s first cutscene plays once per session at the start.' },
    { kind: 'h2', text: 'Intro Sequences' },
    { kind: 'p', text: 'The first seconds of a session, as a layered 2D timeline: sprite, text and particle layers with timed transforms. Sprite layers read from Pixel Studio; text and particle layers work in 3D projects too.' },
  ],
};

const setup: DocPage = {
  path: '/docs/editor/setup', group: 'The editor', label: 'Setup',
  title: 'Setup: Rules, Camera, World, Meta',
  summary: 'Game type, win condition, camera, physics and capabilities.',
  keywords: ['solo', 'co-op', 'versus', 'team', 'matchmaking', 'win condition', 'respawn', 'first person', 'third person', 'top down', 'side view', 'gravity', 'input', 'capabilities', 'localisation'],
  blocks: [
    { kind: 'h2', text: 'Rules' },
    { kind: 'p', text: 'Game type is Solo, Co-op or Versus. Co-op and Versus open a player pool and turn on multiplayer for the published game.' },
    { kind: 'table', head: ['Setting', 'Options', 'Applies to'], rows: [
      ['Team format', '1v1, 2v2, 5v5, FFA, Custom', 'Versus'],
      ['Region', 'EU West (more regions coming)', 'Multiplayer'],
      ['Matchmaking, Skill-based matching', 'On or off', 'Multiplayer'],
      ['Session length, Wins needed', 'Numbers', 'Multiplayer'],
      ['Win condition', 'Objective complete, Score target, Last standing, Race finish, Survival time, Custom (Logic rule)', 'All'],
      ['Switch sides at half', 'On or off', 'Versus'],
      ['Respawn mode', 'Per round, Timed wave, Instant, At checkpoint', 'All'],
      ['Spawn protection', 'Seconds', 'All'],
    ]},
    { kind: 'callout', text: 'Choosing Score target or Survival time writes a starter Logic rule for you, which you can then edit.' },
    { kind: 'h2', text: 'Camera' },
    { kind: 'p', text: 'Pick one of four: 3D first person, 3D third person, 2D top down or 2D side view. This is the camera players get. Crossing between 2D and 3D switches the whole project\'s projection and asks first; nothing is deleted.' },
    { kind: 'h2', text: 'World' },
    { kind: 'ul', items: [
      'Input map: Move (WASD or stick), Interact (E), Primary action (Mouse 1 or R2), Menu (Esc). Rebinding is not yet available. In 2D, only Move applies.',
      'Gravity: the value the runtime uses, default 9.8.',
      'Collision layers: World, Players, Props and Triggers.',
    ]},
    { kind: 'h2', text: 'Meta' },
    { kind: 'ul', items: [
      <>Scripting capabilities: which of Entities, Variables, Audio and Logic effects scripts may use. All four are granted until you change them. See <A to="/docs/editor/code">Code and scripting</A>.</>,
      'Localisation: English (UK) is the source language. More languages are not yet available.',
      'Player pool: a shortcut to hosting for multiplayer projects.',
    ]},
  ],
};

const code: DocPage = {
  path: '/docs/editor/code', group: 'The editor', label: 'Code and scripting',
  title: 'Code and scripting',
  summary: 'HollowScript, the Code workspace, capabilities and running scripts.',
  keywords: ['script', 'hollowscript', 'console', 'run', 'capability', 'entities', 'variables', 'audio', 'sandbox', 'outline', 'problems', 'reference', 'templates'],
  blocks: [
    { kind: 'p', text: 'Most gameplay needs no code. When you want more, the Code workspace is a real script editor for HollowScript, a small language built for exactly this: it can only do what you let it.' },
    { kind: 'h2', text: 'The Code workspace' },
    { kind: 'ul', items: [
      'Scripts: the project\'s script files, with an Outline of the open file\'s declarations underneath. New script offers checked starting templates (run on start, spawn objects, keep score, read input, helper functions, or blank) instead of an empty file. Tick Run on start for scripts that should run when the game begins.',
      'Editor: the script itself, with tabs, live diagnostics, completion, hover help, Find, Go to symbol and a Format action. Saving is automatic a moment after you stop typing; there is no Save button.',
      'Output: what a run printed, the value it returned, and every effect it would have on the scene, filterable and time-stamped.',
      'Problems: every error and warning across every script in the project, grouped by file.',
      'Reference: a searchable list of what HollowScript can do here, built from the project\'s own granted capabilities, so it never lists something that will not run. Clicking an entry inserts it at the cursor.',
    ]},
    { kind: 'h2', text: 'Running a script' },
    { kind: 'p', text: 'Press Run, or Ctrl or Cmd + Enter, and the script runs straight away in a sandbox: nothing it does actually touches the project, and the Output panel shows what it would have done instead. Open Preview and the same Run acts on the live scene for real.' },
    { kind: 'h2', text: 'Capabilities' },
    { kind: 'p', text: 'A script declares what it uses at the top, and it can only use what the project grants in Setup, under Meta.' },
    { kind: 'table', head: ['Capability', 'What it allows'], rows: [
      ['entities', 'Spawn, read, move and remove objects in the running scene.'],
      ['variables', 'Read and write the same match variables Logic rules use.'],
      ['audio', 'Play a one-shot sound.'],
      ['logic', 'Trigger rule effects: announce, give or take items, quests, win or lose.'],
    ]},
    { kind: 'p', text: 'Printing, reading the clock, generating a random number and reading input are always available and never need granting.' },
    { kind: 'code', text: 'uses entities, variables, logic\n\nlet id = effect entities.spawn("crate_stack", 0, 2.0, 0.0, 1.5)\neffect variables.add("crates", 1.0)\neffect logic.announce("Crate delivered")\nreturn effect variables.get("crates")', caption: 'A script that spawns a crate, counts it and tells the player.' },
    { kind: 'p', text: 'A script that asks for a capability the project has not granted fails before it runs, with a message naming the capability.' },
    { kind: 'h2', text: 'The console' },
    { kind: 'p', text: 'Press the backtick key in the editor or in Preview to open a console. It accepts help, clear and stats, and runs anything else as HollowScript against the live scene.' },
    { kind: 'p', text: 'Scripts run in an isolated sandbox, in the editor and in published games, so a script can never reach outside the game it belongs to.' },
  ],
};

const preview: DocPage = {
  path: '/docs/editor/preview', group: 'The editor', label: 'Playtesting',
  title: 'Playtesting with Preview',
  summary: 'Play the project in the tab, with the runtime players get.',
  keywords: ['preview', 'play', 'test', 'pointer lock', 'controls', 'escape'],
  blocks: [
    { kind: 'p', text: 'Preview in the top bar runs the project with the same runtime a published game uses: your HUD, audio, rules, camera choice and gravity. It is chrome-free, so what you see is what a player sees.' },
    { kind: 'ul', items: [
      'In 3D, click to capture the mouse and press Esc to release it. Move with WASD.',
      'In 2D, move with WASD or the arrow keys. A connected gamepad\'s left stick works too.',
      'Press Esc (once the mouse is released) or Exit preview to return to the editor. Nothing is lost.',
      'The backtick key opens the script console over the game.',
    ]},
    { kind: 'p', text: 'An empty project shows a Nothing built yet card with a link to Pixel Studio or Scene, whichever applies.' },
    { kind: 'callout', text: 'Preview is local. Other players are not connected, and nothing you do in Preview affects the published game.' },
  ],
};

/* ── Publishing and players ───────────────────────────────────────────── */
const publishing: DocPage = {
  path: '/docs/publishing', group: 'Publishing and players', label: 'Publishing',
  title: 'Publishing and hosting',
  summary: 'Addresses, visibility, versions, restore and the play page.',
  keywords: ['publish', 'host', 'url', 'address', 'slug', 'public', 'unlisted', 'off', 'private', 'version', 'restore', 'snapshot', 'audit', 'delete', 'domain'],
  blocks: [
    { kind: 'p', text: 'Publishing takes a snapshot of the project and puts it live at its own address. Hosting is included; there is nothing to configure.' },
    { kind: 'h2', text: 'Publish' },
    { kind: 'steps', items: [
      { title: 'From the editor', body: <p>Press Publish in the top bar, choose Public, Unlisted or Off and press Publish. The panel shows the address when it is live, with a Copy button and a link to the live game.</p> },
      { title: 'From the project workspace', body: <p>The Publish page adds a note for the version, the pre-flight audit and the visibility cards. Players get the new version the next time they open the game.</p> },
    ]},
    { kind: 'h2', text: 'Visibility' },
    { kind: 'table', head: ['Setting', 'Who can play', 'Showcase'], rows: [
      ['Public', 'Anyone with the address', 'Listed'],
      ['Unlisted', 'Anyone with the address', 'Hidden'],
      ['Off', 'Nobody. The address shows as unavailable.', 'Hidden'],
    ]},
    { kind: 'p', text: 'Off keeps the address reserved for you. Delete publication, on the Publish page, releases the address and clears the version history; it asks you to type DELETE first.' },
    { kind: 'h2', text: 'The address' },
    { kind: 'p', text: 'The first publish claims an address from the project name. Change the slug on the Settings page at any time after that; the old one stops working.' },
    { kind: 'code', text: 'https://sstk.studio/play/your-game' },
    { kind: 'h2', text: 'Pre-flight audit' },
    { kind: 'p', text: 'The Publish page checks the map before you publish: spawn points, objective sites, lighting and prefabs. Each finding says what to change.' },
    { kind: 'h2', text: 'Versions and restore' },
    { kind: 'ul', items: [
      'Every publish is a numbered version with your note, listed on the Versions page with the live one marked.',
      'Restore copies a version into the editor. Nothing goes live until you publish again.',
      'Scene history keeps up to 20 automatic snapshots, taken while you work and before every publish and restore. Compare shows what was added, removed and changed; Restore brings one back.',
    ]},
    { kind: 'h2', text: 'The play page' },
    { kind: 'p', text: 'Every published game has a public page at its address: the cover, the name and studio, a Play now button, a share field, the wiki if there is one, and a leaderboard once matches are recorded. Play now opens the game in the same tab. Players do not need an account.' },
    { kind: 'h2', text: 'Custom domains' },
    { kind: 'p', text: 'Serving a game from your own domain is planned for the Pro plan and is not yet available.' },
    { kind: 'callout', text: <>Published games can be installed on phones and desktops. See <A to="/docs/publishing/install">Installing a game</A>.</> },
  ],
};

const install: DocPage = {
  path: '/docs/publishing/install', group: 'Publishing and players', label: 'Installing a game',
  title: 'Installing a game',
  summary: 'Add a published game to a home screen or a desktop.',
  keywords: ['install', 'app', 'home screen', 'pwa', 'desktop', 'mobile', 'ios', 'android', 'offline'],
  blocks: [
    { kind: 'p', text: 'Every published game carries an app manifest, so it can be installed like any other app. It opens full screen, with the game\'s name and icon, from the home screen or the desktop.' },
    { kind: 'h2', text: 'On a phone or tablet' },
    { kind: 'table', head: ['Browser', 'How'], rows: [
      ['Safari on iPhone and iPad', 'Open the play page, tap Share, then Add to Home Screen.'],
      ['Chrome on Android', 'Open the play page, tap the menu, then Add to Home screen or Install app.'],
    ]},
    { kind: 'h2', text: 'On a desktop' },
    { kind: 'table', head: ['Browser', 'How'], rows: [
      ['Chrome and Edge', 'Open the play page and press the install icon at the right of the address bar, or choose Install from the menu.'],
      ['Safari on Mac', 'Open the play page, then choose File, Add to Dock.'],
      ['Firefox', 'Bookmark the play page. Firefox does not install web apps on desktop.'],
    ]},
    { kind: 'h2', text: 'What players get' },
    { kind: 'ul', items: [
      'The game launches full screen with no browser chrome.',
      'Updates arrive automatically. Each time the game is opened it loads the version you have published.',
      'A published game still needs a connection to load, so it is not yet playable offline.',
    ]},
    { kind: 'callout', text: 'Export targets on the project Settings page show what is available: the web play address and the installable app are live; a desktop wrapper is coming.' },
  ],
};

const multiplayer: DocPage = {
  path: '/docs/multiplayer', group: 'Publishing and players', label: 'Multiplayer',
  title: 'Multiplayer',
  summary: 'Presence, movement and shared rule state for every project.',
  keywords: ['co-op', 'versus', 'relay', 'presence', 'sync', 'host', 'players', 'pool', 'guest', 'join'],
  blocks: [
    { kind: 'p', text: 'Set the game type to Co-op or Versus in Setup and the published game becomes multiplayer. There is nothing else to author and nothing to host.' },
    { kind: 'h2', text: 'What players get' },
    { kind: 'ul', items: [
      'Joining: players open the address. Each browser gets a guest identity for the game, with no sign-up.',
      'Presence: a list of who is connected.',
      'Movement: every player sees every other player move through the world.',
      'Shared rule state: a variable a rule sets, a quest it starts or completes, a win or a loss reaches every player the moment it happens.',
      'Late joining: someone who joins mid-session inherits the current shared state.',
    ]},
    { kind: 'h2', text: 'How it works, and what it is for' },
    { kind: 'p', text: 'One connected player runs the Logic rules on behalf of the room, chosen the same way by every client, and another takes over within moments if they leave. What players send is forwarded to the room as it is: nothing is simulated or checked by a server.' },
    { kind: 'p', text: 'That makes it a good fit for co-op, exploration and turn-based games, where the point is keeping friends in sync. It is not a fit for competitive or hit-based play, where a modified client could claim a position or an outcome it did not earn.' },
    { kind: 'h2', text: 'Player pool' },
    { kind: 'p', text: 'Every multiplayer project includes a pool of 50 concurrent players. Beyond that, extra players queue rather than degrade the session. Usage shows on the Publish page while the game is live.' },
    { kind: 'h2', text: 'Server-refereed play' },
    { kind: 'p', text: 'Sunder, the platform\'s own tactical shooter, runs on a dedicated server that simulates movement and registers hits. That tier is not yet available to other projects.' },
    { kind: 'p', text: <>Match history and analytics for multiplayer projects are on the <A to="/docs/players">Players page</A>.</> },
  ],
};

const players: DocPage = {
  path: '/docs/players', group: 'Publishing and players', label: 'Players and analytics',
  title: 'Players and analytics',
  summary: 'Match history, player counts and the leaderboard.',
  keywords: ['analytics', 'matches', 'leaderboard', 'stats', 'concurrent'],
  blocks: [
    { kind: 'p', text: 'The Players page in the project workspace shows how a multiplayer game is being played. A Solo project shows an empty state until its game type is changed in Setup.' },
    { kind: 'ul', items: [
      'Players in pool, matches all time, matches in the last 7 days and average match length.',
      'Recent matches: time, mode, map, result and duration for the last eight. Team scores show for Versus games.',
      'Concurrent players appear once the game is live.',
    ]},
    { kind: 'h2', text: 'The leaderboard' },
    { kind: 'p', text: 'The play page shows a leaderboard with rank, player, matches, wins and score once matches are recorded for the game.' },
  ],
};

const wiki: DocPage = {
  path: '/docs/wiki', group: 'Publishing and players', label: 'Game wikis',
  title: 'Game wikis',
  summary: 'A hosted, multi-page wiki for every game.',
  keywords: ['wiki', 'markdown', 'pages', 'lore', 'guide', 'directory', 'images'],
  blocks: [
    { kind: 'p', text: 'A project can have a wiki: lore, guides and reference pages hosted at their own address, linked from the play page and the showcase.' },
    { kind: 'code', text: 'https://sstk.studio/wiki/your-game' },
    { kind: 'h2', text: 'Creating and editing' },
    { kind: 'ul', items: [
      'Create the wiki from the project\'s Wiki page. Give it a title; the address follows the game.',
      'Pages have a title, an optional section for grouping in the sidebar, a slug and a markdown body with a live preview.',
      'Drop an image onto the editor to upload it, up to 2 MB.',
      'Save is explicit. Leaving with unsaved changes asks first.',
      'Reorder pages, and restore an earlier version of a page from its history.',
    ]},
    { kind: 'h2', text: 'Visibility' },
    { kind: 'table', head: ['Setting', 'Effect'], rows: [
      ['Public', 'Listed in the wiki directory; anyone with the link can read it.'],
      ['Unlisted', 'Reachable by link only.'],
      ['Off', 'The address shows as unavailable.'],
    ]},
    { kind: 'p', text: 'The wiki\'s visibility is separate from the game\'s. Readers get a sidebar of pages by section and a search box for that wiki. All public wikis are listed at the wiki directory.' },
  ],
};

const showcase: DocPage = {
  path: '/docs/showcase', group: 'Publishing and players', label: 'Showcase and profile',
  title: 'Showcase and developer profile',
  summary: 'Where public games are listed, and your studio page.',
  keywords: ['showcase', 'profile', 'studio', 'developer', 'public', 'featured'],
  blocks: [
    { kind: 'p', text: 'The showcase lists every game published as Public, with search and Originals and Community tabs. A game card shows the cover, the name, the studio and a link to its wiki if it has one.' },
    { kind: 'h2', text: 'Your developer profile' },
    { kind: 'p', text: 'Every studio has a public page listing its games, with a banner, a bio and a website link. Edit it under Account settings, Public profile: studio name, profile address, bio, website and banner.' },
    { kind: 'p', text: 'The profile address is where the by-line on every play page and showcase card points.' },
  ],
};

/* ── Account ──────────────────────────────────────────────────────────── */
const account: DocPage = {
  path: '/docs/account', group: 'Account', label: 'Account and sign-in',
  title: 'Account and sign-in',
  summary: 'Sign-in methods, your profile, tokens, connected games and team.',
  keywords: ['sign in', 'sign up', 'google', 'password', 'reset', 'verify', 'profile', 'api token', 'connected games', 'team', 'billing', 'delete account'],
  blocks: [
    { kind: 'h2', text: 'Signing in' },
    { kind: 'ul', items: [
      'Continue with Google, or use an email address and a password.',
      'Email sign-ups confirm the address with a code sent to it.',
      'Forgotten a password? Reset it with a code sent to your email.',
      'Sign-up asks for a studio name. It becomes your public developer name and can be changed later.',
    ]},
    { kind: 'h2', text: 'Account settings' },
    { kind: 'table', head: ['Section', 'What it holds'], rows: [
      ['Profile', 'Display name, email and a change-password link.'],
      ['Public profile', 'Studio name, profile address, bio, website and banner for your showcase page.'],
      ['Billing and plan', 'Plans are not yet available. Everything is free during early access.'],
      ['API tokens', 'Named tokens for tools that need to act on your behalf. The secret is shown once; revoke any time.'],
      ['Connected games', 'Soulstack games you have signed into with this account. Disconnecting keeps your saved progress.'],
      ['Team', 'Your team name and seats. One seat today; more seats arrive with team plans.'],
      ['Danger zone', 'Delete your account.'],
    ]},
    { kind: 'h2', text: 'Playing with one account' },
    { kind: 'p', text: 'Some Soulstack games let you sign in with your Studio account, so progress follows you without a separate password. They appear under Connected games.' },
  ],
};

/* ── Reference ────────────────────────────────────────────────────────── */
const shortcuts: DocPage = {
  path: '/docs/reference/shortcuts', group: 'Reference', label: 'Keyboard shortcuts',
  title: 'Keyboard shortcuts',
  summary: 'Every binding in the editor, Map Builder, Pixel Studio and Preview.',
  keywords: ['keys', 'hotkeys', 'bindings', 'undo', 'redo', 'copy', 'paste'],
  blocks: [
    { kind: 'p', text: 'Shortcuts apply while the editor has focus and you are not typing in a field. Cmd on a Mac, Ctrl elsewhere.' },
    { kind: 'h2', text: 'Scene' },
    { kind: 'table', head: ['Key', 'Action'], rows: [
      ['V, G, R, S', 'Select, Move, Rotate, Scale tool'],
      ['Q, E', 'Rotate the selection or a pending placement by 45 degrees'],
      ['Arrow keys', 'Nudge by one grid step; hold Shift for ten'],
      ['F', 'Focus the camera on the selection'],
      ['Ctrl + Z, Ctrl + Shift + Z or Ctrl + Y', 'Undo, redo'],
      ['Ctrl + S', 'Save now'],
      ['Ctrl + A', 'Select everything on the current floor'],
      ['Ctrl + D', 'Duplicate the selection'],
      ['Ctrl + C, Ctrl + V', 'Copy, paste at the cursor'],
      ['Delete or Backspace', 'Delete the selection'],
      ['Esc', 'Cancel a pending placement, otherwise clear the selection'],
      ['Right-click + WASD', 'Free-fly the camera'],
      ['Mouse wheel', 'Zoom'],
      ['Shift + mouse move', 'Precision orbit or pan'],
    ]},
    { kind: 'h2', text: 'Everywhere in the editor' },
    { kind: 'table', head: ['Key', 'Action'], rows: [
      ['?', 'Open the shortcut guide'],
      ['`', 'Open the script console'],
      ['Ctrl + Shift + F', 'Distraction-free mode'],
      ['Enter, Esc', 'Confirm or cancel a dialog'],
    ]},
    { kind: 'h2', text: 'Map Builder' },
    { kind: 'table', head: ['Key', 'Action'], rows: [
      ['Ctrl + Z', 'Undo the last structural edit'],
    ]},
    { kind: 'h2', text: 'Pixel Studio' },
    { kind: 'table', head: ['Key', 'Action'], rows: [
      ['B, E, G, I', 'Pencil, eraser, fill, pick'],
      ['Ctrl + Z, Ctrl + Shift + Z or Ctrl + Y', 'Undo, redo'],
    ]},
    { kind: 'h2', text: 'Code' },
    { kind: 'table', head: ['Key', 'Action'], rows: [
      ['Ctrl + Enter', 'Run the open script'],
      ['Ctrl + S', 'Save now (scripts also autosave)'],
      ['Ctrl + F', 'Find in the open script'],
      ['Ctrl + Shift + O', 'Go to symbol'],
      ['Shift + Alt + F', 'Format the open script'],
      ['F2', 'Rename the selected script'],
    ]},
    { kind: 'h2', text: 'Console' },
    { kind: 'table', head: ['Key', 'Action'], rows: [
      ['Enter, Shift + Enter', 'Run a console line, insert a newline'],
    ]},
    { kind: 'h2', text: 'Preview and published games' },
    { kind: 'table', head: ['Key', 'Action'], rows: [
      ['WASD or arrow keys, left stick', 'Move'],
      ['E', 'Interact (3D)'],
      ['Mouse 1 or R2', 'Primary action (3D)'],
      ['Esc', 'Release the mouse, then leave Preview'],
    ]},
  ],
};

const limits: DocPage = {
  path: '/docs/reference/limits', group: 'Reference', label: 'Limits and formats',
  title: 'Limits and formats',
  summary: 'File types, sizes and counts in one table.',
  keywords: ['size', 'maximum', 'format', 'quota', 'mb', 'gb'],
  blocks: [
    { kind: 'table', head: ['What', 'Limit'], rows: [
      ['Project storage', '10 GB per project'],
      ['Model upload', 'GLB, single scene, triangulated meshes, 10 MB'],
      ['Image or texture upload', 'PNG, JPEG, WebP, 8 MB'],
      ['Audio upload', 'MP3, OGG, WAV, 5 MB'],
      ['Cover image', 'PNG, JPEG, WebP, 2 MB'],
      ['Wiki image', '2 MB'],
      ['Sprite canvas', '16 to 64 pixels preset, or custom'],
      ['Spritesheet import', 'Cells of 4 to 128 pixels, up to 512 cells'],
      ['Cutscene length', '6 seconds'],
      ['Scene history', '20 automatic snapshots'],
      ['Player pool', '50 concurrent players included'],
      ['Editor window', 'At least 860 pixels wide'],
    ]},
  ],
};

const reference: DocPage = {
  path: '/docs/reference', group: 'Reference', label: 'Engine API',
  title: 'Engine API',
  summary: 'For teams embedding the engine in their own code.',
  keywords: ['api', 'build-kit', 'runtime', 'registry', 'library', 'package'],
  blocks: [
    { kind: 'p', text: 'Studio is built on a reusable engine, published as a package for teams who want to run engine content in their own web app. Most creators never need it; the editor and the play page cover the whole workflow.' },
    { kind: 'h2', text: 'Runtime' },
    { kind: 'code', text: '<GameRuntime mapDocument config mode projection />', caption: 'Renders a map document as a scene, in edit or play mode, in 2D or 3D.' },
    { kind: 'code', text: 'deriveMapData(doc: MapDocument): MapRuntimeData', caption: 'Resolves walkable space, spawns and collision from a map. The same derivation runs in the editor and in published games.' },
    { kind: 'code', text: '<FirstPersonController config mapData callbacks />', caption: 'Pointer-locked first-person movement against derived map data.' },
    { kind: 'h2', text: 'Registries' },
    { kind: 'code', text: 'registerObjects(definitions: ObjectDefinition[]): void', caption: 'Adds object types. Registered objects appear in the editor palette with an inspector built from their parameter schema.' },
    { kind: 'code', text: 'NEUTRAL_BIPED_DEFINITION: CharacterDefinition', caption: 'The stock rigged biped and its 45 clips.' },
    { kind: 'h2', text: 'Entry points' },
    { kind: 'table', head: ['Import', 'Contents'], rows: [
      ['@sstk/build-kit', 'Runtime, registries, editor overlay, audio, core assets'],
      ['@sstk/build-kit/2d', 'Tilemap and sprite scenes'],
      ['@sstk/build-kit/logic', 'The shared Logic rule runtime'],
      ['@sstk/build-kit/scripting', 'HollowScript capabilities and runtime'],
    ]},
    { kind: 'p', text: <>The shape of a project\'s saved data is in <A to="/docs/reference/content-schema">Content schema</A>.</> },
  ],
};

const contentSchema: DocPage = {
  path: '/docs/reference/content-schema', group: 'Reference', label: 'Content schema',
  title: 'Content schema',
  summary: 'The shape of a project\'s saved data.',
  keywords: ['map document', 'trigger volume', 'mode document', 'project record', 'schema', 'json'],
  blocks: [
    { kind: 'p', text: 'A project is a map document, one document per Systems and Setup panel, and a project record that ties them together. Exported .sstk files contain all of them.' },
    { kind: 'h2', text: 'Map document' },
    { kind: 'table', head: ['Field', 'Holds'], rows: [
      ['floors', 'Panels, walls, rooms and stairs per floor.'],
      ['objectInstances', 'Placed assets: asset id, transform and per-instance parameters.'],
      ['triggers', 'Trigger volumes: a kind, a size, a zone id and parameters.'],
      ['lights, markers, decals', 'Point, spot and ambient-override lights; spawn, checkpoint, event and custom markers; decals.'],
      ['tilemap2d', '2D projects: the tilemap, its mode, tiles and placed entities.'],
    ]},
    { kind: 'p', text: 'Map documents are versioned. An older document is migrated forward when it is opened.' },
    { kind: 'h2', text: 'Mode documents' },
    { kind: 'table', head: ['Document', 'Authored in'], rows: [
      ['logic', 'Logic rules'],
      ['hud', 'HUD layout'],
      ['audio', 'Mixer levels and zones'],
      ['fx', 'Material and particle presets'],
      ['gamedata', 'Items, quests, dialogue'],
      ['pixel', 'Sprites, palettes and clips'],
      ['model', 'Model Studio elements'],
      ['gamesetup', 'Rules, camera, world and capabilities'],
      ['prefabs', 'Saved prefabs'],
      ['cutscenes, introsequences', 'Timelines'],
      ['scripts', 'HollowScript files'],
    ]},
    { kind: 'h2', text: 'Project record' },
    { kind: 'table', head: ['Field', 'Holds'], rows: [
      ['meta', 'Id, name, owner, template and timestamps.'],
      ['config', 'Projection (2D or 3D), theme, player overrides and the cover image.'],
      ['characters, poseLibraries, rigBones', 'The roster and custom rig data.'],
      ['assets', 'Uploaded model, image and audio records.'],
    ]},
  ],
};

export const DOC_PAGES: DocPage[] = [
  overview, quickstart, projects,
  editor, scene, art, characters, logic, systems, setup, code, preview,
  publishing, install, multiplayer, players, wiki, showcase,
  account,
  shortcuts, limits, reference, contentSchema,
];

/** Old docs addresses that still work, sent to the page that replaced them. */
export const DOC_REDIRECTS: Record<string, string> = {
  '/docs/guide': '/docs/editor/scene',
  '/docs/guide/posing-characters': '/docs/editor/characters',
  '/docs/guide/wiring-triggers': '/docs/editor/logic',
  '/docs/guide/multiplayer': '/docs/multiplayer',
};
